import { useTranslation } from 'react-i18next';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';
import { InquiryResourceField } from '@/models/common/InquiryResourceField';
import { ResourceTypeLabels } from '@/models/common/InquiryEnums';
import styles from './InquiryResourceSummary.module.scss';

/**
 * What the inquiry was opened about — the transaction, the supported, the support request —
 * as it was when the coordinator asked.
 *
 * Why it is worth a block of its own: without it a secretary reading "בירור על תנועה מ-12.08"
 * has a subject and a link, and has to open the transactions board in another tab to learn
 * which transaction, for how much, from whom. Every inquiry, every time.
 *
 * The fields come from the snapshot stored on the inquiry, not from a live lookup, so a
 * transaction re-classified since does not silently change what the thread appears to be
 * about. Inquiries opened before the snapshot existed have none — then only the link renders,
 * which is what the screen did before.
 */

interface Props {
  resourceType: string | null;
  resourceId: string | null;
  fields: InquiryResourceField[] | undefined;
  /** Absent for the coordinator: the resource routes here are the office's. */
  onOpenResource?: () => void;
  /** The office blade is narrow; the coordinator's thread header is not. */
  dense?: boolean;
}

export default function InquiryResourceSummary({
  resourceType, resourceId, fields, onOpenResource, dense = false,
}: Props) {
  const { t } = useTranslation();

  // No resource at all: a general inquiry, and there is nothing to describe.
  if (!resourceType || !resourceId) return null;

  const hasFields = !!fields && fields.length > 0;
  const typeLabel = t(ResourceTypeLabels[resourceType] || resourceType);

  return (
    <div className={dense ? styles.cardDense : styles.card}>
      <div className={styles.head}>
        <ReceiptLongOutlinedIcon sx={{ fontSize: dense ? 15 : 16 }} />
        <span className={dense ? 'body-xs-sm-regular' : 'body-sm-semibold'}>
          {t('inquiries.resource.heading', { type: typeLabel })}
        </span>

        {onOpenResource ? (
          <button type="button" className={`body-xs-sm-regular ${styles.link}`} onClick={onOpenResource}>
            #{resourceId}
            <OpenInNewIcon sx={{ fontSize: 12 }} />
          </button>
        ) : (
          <span className={`body-xs-sm-regular ${styles.idOnly}`}>#{resourceId}</span>
        )}
      </div>

      {hasFields ? (
        <div className={styles.grid}>
          {fields.map((field) => (
            // Label as the key: the snapshot has one line per field, and ids would be noise
            <div key={field.label} className={styles.field}>
              <span className={`body-xs-sm-regular ${styles.fieldLabel}`}>{field.label}</span>
              <span className={`body-sm-semibold ${styles.fieldValue}`}>{field.value}</span>
            </div>
          ))}
        </div>
      ) : (
        /*
          * Said out loud rather than left blank: an inquiry from before the snapshot existed
          * would otherwise look like one whose details failed to load.
          */
        <span className={`body-xs-sm-regular ${styles.noFields}`}>
          {t('inquiries.resource.noSnapshot')}
        </span>
      )}
    </div>
  );
}
